import { zodResolver } from '@hookform/resolvers/zod';
import { validateBr } from 'js-brasil';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import * as z from 'zod';

import { selectUser } from '../../features/auth/authSlice';
import { useFiscalValidateMutation } from '../../features/exam/certificationApiSlice';
import { useGetGlobalQuery } from '../../features/my-certifications/myCertificationsApiSlice';
import { FiscalValidationOutput } from '../../types/certifications/FiscalValidation';
import { sanitizeCpf } from '../../utils/sanitize-cpf';

export type FiscalAuthorizationProps = {
  certificationId: string;
  onFiscalAuthorization: (fiscalApproved: boolean) => void;
  certificationLog?: string;
  stepLabel?: string;
  onBack?: () => void;
};

const FiscalAuthorizationSchema = z.object({
  login: z
    .string()
    .min(1, 'CPF obrigatório')
    .refine(value => validateBr.cpf(value), 'CPF inválido.'),
  password: z.string().min(1, 'Senha obrigatória'),
  accept: z
    .boolean()
    .refine(value => value, 'Você precisa aceitar os termos do fiscal.'),
});

type FiscalAuthorizationForm = z.infer<typeof FiscalAuthorizationSchema>;

function getFiscalValidationErrorMessage(
  status: FiscalValidationOutput['status'],
) {
  switch (status) {
    case 'error':
      return 'Ocorreu um erro! tente novamente mais tarde!';
    case 'fiscal_not_found':
      return 'Fiscal não encontrado.';
    case 'login_fiscal_error':
      return 'Usuário ou senha inválidos.';
    case 'certification_log_not_found':
      return 'Certificação não encontrado.';
    case 'fiscal_is_player':
      return 'O usuário não pode ser fiscal da própria prova.';
    default:
      return 'Ocorreu um erro! tente novamente mais tarde!';
  }
}

export function FiscalAuthorization({
  onFiscalAuthorization,
  certificationLog,
  stepLabel = 'Etapa 3 de 3',
  onBack,
}: FiscalAuthorizationProps) {
  const [validateFiscal] = useFiscalValidateMutation();
  const user = useSelector(selectUser);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FiscalAuthorizationForm>({
    mode: 'onSubmit',
    resolver: zodResolver(FiscalAuthorizationSchema),
    defaultValues: {
      login: '',
      password: '',
      accept: false,
    },
  });

  const { data: globalData } = useGetGlobalQuery({ apiKey: user?.apiKey }, {});

  async function handleFiscalAuthorization({
    login,
    password,
  }: FiscalAuthorizationForm) {
    try {
      const resp = await validateFiscal({
        login: sanitizeCpf(login),
        password,
        certificationLogId: certificationLog,
      });

      if ('data' in resp && resp?.data?.status === 'approved') {
        onFiscalAuthorization(true);
      } else if ('data' in resp && resp?.data?.status) {
        const message = getFiscalValidationErrorMessage(resp.data.status);

        toast.error(message, {
          position: 'top-center',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-8">
      <div className="mx-auto max-w-5xl rounded-3xl border border-slate-200 bg-white p-8 shadow-xl">
        <div className="flex flex-col gap-6 border-b border-slate-200 pb-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-3">
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
              {stepLabel}
            </p>
            <h1 className="text-3xl font-bold text-slate-900">
              Autorização do fiscal
            </h1>
            <p className="max-w-2xl text-sm text-slate-600">
              O fiscal responsável deve conferir as credenciais e aceitar os
              termos antes de liberar a entrada do candidato na prova.
            </p>
          </div>

          <div className="rounded-2xl bg-slate-900 p-5 text-white">
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
              Estado atual
            </p>
            <p className="mt-2 text-lg font-semibold">
              {isSubmitting ? 'Validando credenciais' : 'Aguardando validação'}
            </p>
            <p className="mt-2 max-w-xs text-sm text-slate-300">
              {isSubmitting
                ? 'Aguarde enquanto validamos os dados informados pelo fiscal.'
                : 'Somente após a aprovação do fiscal a prova será liberada.'}
            </p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
          <form
            className="space-y-4"
            onSubmit={handleSubmit(handleFiscalAuthorization)}
          >
            <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5 text-sm text-blue-900">
              Solicite ao fiscal a liberação para realizar a prova usando o CPF
              e a senha da conta de supervisão.
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <label
                htmlFor="login"
                className="text-sm font-semibold text-slate-900"
              >
                CPF do fiscal
              </label>
              <input
                id="login"
                type="text"
                inputMode="numeric"
                autoComplete="username"
                aria-invalid={errors.login ? 'true' : 'false'}
                placeholder="000.000.000-00"
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-800 focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700"
                {...register('login')}
              />
              {errors.login && (
                <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {errors.login.message}
                </p>
              )}

              <label
                htmlFor="password"
                className="mt-5 block text-sm font-semibold text-slate-900"
              >
                Senha
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={errors.password ? 'true' : 'false'}
                placeholder="Digite a senha do fiscal"
                className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-800 focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700"
                {...register('password')}
              />
              {errors.password && (
                <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {errors.password.message}
                </p>
              )}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <label
                htmlFor="accept"
                className="flex items-start gap-3 text-sm text-slate-700"
              >
                <input
                  id="accept"
                  type="checkbox"
                  aria-invalid={errors.accept ? 'true' : 'false'}
                  className="mt-1 rounded border-slate-300 text-blue-700 focus:ring-blue-500"
                  {...register('accept')}
                />
                <span>
                  Declaro que li e aceito os{' '}
                  <a
                    href={globalData?.term_supervisor?.url}
                    target="_blank"
                    rel="noreferrer"
                    className="font-semibold text-blue-700 underline"
                  >
                    termos de responsabilidades
                  </a>{' '}
                  de fiscal de prova.
                </span>
              </label>
              {errors.accept && (
                <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {errors.accept.message}
                </p>
              )}
            </div>
          </form>

          <div className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="grid gap-3 rounded-xl bg-slate-900 p-4 text-white sm:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-400">
                    Etapa
                  </p>
                  <p className="mt-1 text-sm font-semibold">
                    Autorização separada do fiscal
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.15em] text-slate-400">
                    Liberação
                  </p>
                  <p className="mt-1 text-sm font-semibold">
                    Avanço automático após aprovação
                  </p>
                </div>
              </div>

              <div className="mt-4 space-y-3">
                {onBack && (
                  <button
                    type="button"
                    onClick={onBack}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                  >
                    Voltar
                  </button>
                )}
                <button
                  type="submit"
                  onClick={handleSubmit(handleFiscalAuthorization)}
                  disabled={isSubmitting}
                  className="w-full rounded-xl bg-blue-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmitting ? 'Validando fiscal...' : 'Validar fiscal'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
